import React, { useState, useEffect } from 'react';
import { usersAPI, messagesAPI } from '../utils/api';
import { useSocket } from '../context/SocketContext';
import Avatar from './Avatar';
import toast from 'react-hot-toast';

export default function BroadcastModal({ onClose }) {
  const { isUserOnline } = useSocket();
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deptFilter, setDeptFilter] = useState('All');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([usersAPI.getAll(), usersAPI.getDepartments()])
      .then(([empRes, deptRes]) => {
        setEmployees(empRes.data.data);
        setDepartments(['All', ...deptRes.data.data]);
      })
      .catch(() => toast.error('Failed to load employees.'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = id => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const selectAll = () => {
    const ids = filtered.map(e => e._id);
    setSelected(ids);
  };
  const clearAll = () => setSelected([]);

  const filtered = employees.filter(e => {
    const matchSearch = e.name?.toLowerCase().includes(search.toLowerCase()) || e.department?.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === 'All' || e.department === deptFilter;
    return matchSearch && matchDept;
  });

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Please enter a message.'); return; }
    if (selected.length === 0) { toast.error('Select at least 1 recipient.'); return; }
    setSending(true);
    try {
      const res = await messagesAPI.broadcast({ receiverIds: selected, content: message.trim() });
      toast.success(res.data.message);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send broadcast.');
    } finally { setSending(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <div className="modal-title">📢 Broadcast Message</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Send the same message to multiple employees at once</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Message */}
          <div className="form-group" style={{ marginBottom: 16 }}>
            <label className="form-label">Message *</label>
            <textarea
              className="form-input"
              style={{ height: 'auto', padding: '10px 12px', resize: 'vertical', minHeight: 80 }}
              placeholder="Type your message to broadcast…"
              value={message} onChange={e => setMessage(e.target.value)} rows={3} maxLength={2000}
            />
            <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right', marginTop: 3 }}>{message.length}/2000</div>
          </div>

          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ flex: 1, height: 34, minWidth: 140 }} placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} />
            <select className="form-input" style={{ height: 34, width: 'auto' }} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
              {departments.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>

          {/* Select All / Clear */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{selected.length} of {filtered.length} selected</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={selectAll}>Select All</button>
              <button className="btn btn-ghost btn-sm" onClick={clearAll}>Clear</button>
            </div>
          </div>

          {/* Employee List */}
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1.5px solid var(--border)', borderRadius: 8 }}>
            {loading ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No employees found.</div>
            ) : filtered.map(emp => (
              <div key={emp._id} onClick={() => toggle(emp._id)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-soft)', background: selected.includes(emp._id) ? 'var(--brand-light)' : 'transparent', transition: 'background 0.15s' }}>
                <input type="checkbox" checked={selected.includes(emp._id)} onChange={() => {}} style={{ accentColor: 'var(--brand)', width: 15, height: 15, flexShrink: 0 }} />
                <Avatar user={emp} size="sm" showStatus isOnline={isUserOnline(emp._id)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{emp.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{emp.department}</div>
                </div>
                <span style={{ fontSize: 11, color: isUserOnline(emp._id) ? 'var(--online)' : 'var(--offline)', fontWeight: 600 }}>
                  {isUserOnline(emp._id) ? '● Online' : '● Offline'}
                </span>
              </div>
            ))}
          </div>

          {selected.length > 0 && (
            <div className="alert alert-info" style={{ marginTop: 12, fontSize: 12 }}>
              This message will be sent as an individual direct message to each of the {selected.length} selected employees.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSend} disabled={sending || !message.trim() || selected.length === 0}>
            {sending ? 'Sending…' : `📢 Send to ${selected.length} people`}
          </button>
        </div>
      </div>
    </div>
  );
}
